import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, User, LogOut, Plus, BarChart3, Hash } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fashion-social-hub-2/artifacts/skrs743x_meetmrfashion_text.jpg";

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isAdmin = user?.username === 'meetmrfashion';

  const navLinks = [
    { label: 'Feed', href: '/feed' },
    { label: 'Brands', href: '/brands' },
    { label: 'Challenges', href: '/challenges' },
    { label: '#RunwayToRealLife', href: '/campaign/runway-to-real-life' },
    { label: 'The Book', href: '/book' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-xl border-b border-white/5">
      <div className="h-20 flex items-center justify-between px-6 md:px-12">
        {/* Logo */}
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <div className="h-12 overflow-hidden rounded-sm bg-gray-600/50 p-1">
            <img 
              src={LOGO_URL} 
              alt="Meet Mr. Fashion" 
              className="h-full w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="nav-link text-sm uppercase tracking-[0.15em] text-gray-400 hover:text-white transition-colors duration-300"
              data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop Auth/User */}
        <div className="hidden md:flex items-center gap-4">
          {isAuthenticated ? (
            <>
              <Link to="/create" data-testid="create-look-btn">
                <Button variant="outline" size="sm" className="gap-2 border-white/20 hover:border-white text-white">
                  <Plus className="w-4 h-4" />
                  <span className="uppercase text-xs tracking-wider">Share Look</span>
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-[#C41E3A] flex items-center justify-center">
                      {user?.avatar_url ? (
                        <img src={user.avatar_url} alt={user.username} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-sm font-medium text-white">{user?.username?.[0]?.toUpperCase()}</span>
                      )}
                    </div>
                    <span className="text-sm">{user?.username}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#1c1c1c] border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to={`/profile/${user?.username}`} className="flex items-center gap-2" data-testid="profile-link">
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/analytics" className="flex items-center gap-2" data-testid="analytics-link">
                        <BarChart3 className="w-4 h-4" />
                        Analytics
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-red-400" data-testid="logout-btn">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link to="/auth" data-testid="login-btn">
              <Button className="btn-luxury btn-luxury-secondary">
                Sign In
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-t border-white/5 animate-fade-in">
          <nav className="flex flex-col p-6 gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-lg text-gray-300 hover:text-white py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            {isAuthenticated ? (
              <>
                <Link
                  to="/create"
                  className="text-lg text-gray-300 hover:text-white py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Share Look
                </Link>
                <Link
                  to={`/profile/${user?.username}`}
                  className="text-lg text-gray-300 hover:text-white py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Profile
                </Link>
                {isAdmin && (
                  <Link
                    to="/analytics"
                    className="text-lg text-[#C41E3A] py-2 flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <BarChart3 className="w-5 h-5" />
                    Analytics
                  </Link>
                )}
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="text-lg text-red-400 py-2 text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/auth"
                className="text-lg text-[#C41E3A] py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign In
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
